import React from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const MODULES = {
  toolbar: [
    ['bold', 'italic', 'underline'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link'],
    ['clean'],
  ],
};

/**
 * The formats Quill is allowed to produce, kept deliberately in step with
 * `ALLOWED_HTML_TAGS` in the backend sanitisers (prod-nimbus-ai-use-cases,
 * prod-nimbus-meeting-minutes, prod-nimbus-processing-activities, inittrack-api).
 *
 * Without a `formats` prop, Quill 1.3.7 enables its **entire** registry regardless of what
 * the toolbar shows — so a paste, or Tab (bound to indent by the default keyboard module),
 * could produce headings, blockquotes, code blocks, images, colours, alignment and indent
 * classes. Every one of those was then stripped server-side, and the user's formatting
 * vanished silently on save. Restricting the editor is the half of that fix the editor owns;
 * widening the allowlist to the structural tags worth keeping is the half the sanitiser owns.
 *
 * Deliberately excluded: `image` (Quill inlines it as a base64 data URI, which both defeats
 * the sanitiser's URL allowlist and pushes the record towards DynamoDB's 400KB item limit),
 * and `align`/`indent`/`color`/`background`/`font`/`size`, which Quill expresses as classes
 * or inline styles — the sanitiser drops every attribute, so they could never survive.
 */
const FORMATS = [
  'bold', 'italic', 'underline', 'strike',
  'list', 'link',
  'header', 'blockquote', 'code-block', 'code',
  'script',
];

interface RichTextFieldProps {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  height?: number;
}

export const RichTextField: React.FC<RichTextFieldProps> = ({ value, onChange, placeholder, height = 100 }) => (
  <div className="rich-text-field" style={{ ['--rtf-height' as any]: `${height}px` }}>
    <ReactQuill
      theme="snow"
      value={value}
      // react-quill's own mount call to `setContents` carries no explicit source, which
      // Quill defaults to 'api' — so every mount fires onChange once, even with nothing typed
      // and nothing changed. Forwarding that unfiltered dirties whatever field this is bound
      // to before the user does anything. `source !== 'user'` is the same guard
      // DocumentDetail.tsx:719 already uses for its own ReactQuill.
      onChange={(html, _delta, source) => { if (source === 'user') onChange(html); }}
      placeholder={placeholder}
      modules={MODULES}
      formats={FORMATS}
    />
  </div>
);

/**
 * The named entities that decode to something the server's `\s` treats as whitespace.
 *
 * Not exhaustive — `html.unescape` knows all ~2,200 HTML5 names — but exhaustive over the
 * ones that *matter*, because only these change the empty/non-empty verdict. Every other
 * name (`&ndash;`, `&copy;`) decodes to visible content, and leaving it as the literal text
 * `&ndash;` reaches the same verdict by a different route.
 *
 * Deliberately absent: `&zwnj;` `&zwj;` `&ZeroWidthSpace;` `&#8203;`. Python's `\s` does not
 * match U+200B–U+200D either, so the server reads `<p>&#8203;</p>` as content — and a client
 * that disagreed, even in the cautious direction, is exactly how this defect started.
 */
const NAMED_WHITESPACE: Record<string, string> = {
  // All 16 HTML5 names that decode to whitespace and nothing else. Enumerated from Python's
  // html.entities.html5 rather than written from memory, because seven of them are CamelCase
  // aliases (&NonBreakingSpace; &ThickSpace; &ThinSpace; &Tab; &NewLine; &MediumSpace;
  // &VeryThinSpace;) and a first pass that lower-cased the lookup missed every one.
  nbsp: ' ', NonBreakingSpace: ' ',
  ensp: ' ', emsp: ' ', emsp13: ' ', emsp14: ' ',
  numsp: ' ', puncsp: ' ',
  thinsp: ' ', ThinSpace: ' ',
  hairsp: ' ', VeryThinSpace: ' ',
  MediumSpace: ' ', ThickSpace: ' ',
  Tab: '\t', NewLine: '\n',
  // The basic five, so '<p>&amp;</p>' stays content rather than the literal '&amp;'.
  amp: '&', lt: '<', gt: '>', quot: '"', apos: "'",
};

/**
 * A numeric character reference, decoded the way the server does.
 *
 * Two cases the obvious `String.fromCodePoint` gets wrong:
 *
 * - **Out of range.** `&#x110000;` makes `fromCodePoint` throw `RangeError`, which would take
 *   the whole form down from a paste. `html.unescape` yields U+FFFD, so we do too.
 * - **The C1 range.** HTML5 requires 0x80–0x9F to be reinterpreted through Windows-1252, and
 *   `html.unescape` implements that — so the server reads `&#133;` as U+2026 '…', content.
 *   Decoding it literally gives U+0085, which is whitespace to Python and would read as empty.
 *   Since this function only ever computes a boolean and `text` never leaves it, the whole
 *   range collapses to one content marker rather than carrying the 32-entry cp1252 table.
 */
const isInvalidCodepoint = (cp: number): boolean =>
  (cp >= 0x01 && cp <= 0x08) || cp === 0x0b || (cp >= 0x0e && cp <= 0x1f) || cp === 0x7f
  || (cp >= 0xfdd0 && cp <= 0xfdef)
  || (cp & 0xfffe) === 0xfffe;   // every plane's last two: 0xFFFE/0xFFFF ... 0x10FFFE/0x10FFFF

const decodeNumeric = (cp: number): string => {
  if (!Number.isFinite(cp) || cp < 0 || cp > 0x10ffff) return '\uFFFD';
  if (cp >= 0x80 && cp <= 0x9f) return '\uFFFD';
  if (isInvalidCodepoint(cp)) return '';
  return String.fromCodePoint(cp);
};

/**
 * Whether a rich-text value holds anything a human would call content.
 *
 * This has to agree with `strip_html` in the Lambdas, and until 2026-08-04 it did not. It
 * stripped tags and trimmed but never decoded entities, while the server unescapes and
 * normalises `\xa0`. So `<p>&nbsp;</p>` — which is what a Quill editor holds after you type a
 * space and delete the word — read as CONTENT on the client and as EMPTY on the server. An
 * adversarial differential over 6,000 cases found 143 where the client enabled Submit and the
 * server then refused it, every one of this class, and it became load-bearing the moment the
 * AI Act high-risk gate started requiring a classification rationale.
 *
 * Mirrors the server step for step: tags become a space (so '<p>a</p><p>b</p>' is not 'ab'),
 * entities are decoded, then collapse and trim. The extra codepoints in the final character
 * class are the ones Python's `\s` matches and JavaScript's does not — without them a pasted
 * U+0085 would enable Submit on a field the server considers blank. The gap in the other
 * direction (JS `\s` matches U+FEFF, Python's does not) is closed just above.
 *
 * Deliberately pure string work rather than DOMParser: this runs in Node too, inside the
 * headless PDF harnesses, where there is no DOM.
 */
export const isRichTextEmpty = (html: string): boolean => {
  if (!html) return true;
  const text = html
    .replace(/<[^>]*>/g, ' ')
    .replace(/&#x([0-9a-f]+);/gi, (_, hex) => decodeNumeric(parseInt(hex, 16)))
    .replace(/&#(\d+);/g, (_, dec) => decodeNumeric(parseInt(dec, 10)))
    // Case-SENSITIVE: '&NBSP;' is not an HTML5 entity, so the server leaves it as literal
    // text — i.e. content. Folding case here would decode it and call the field empty.
    .replace(/&([A-Za-z][A-Za-z0-9]*);/g, (m, name) => NAMED_WHITESPACE[name] ?? m)
    // JavaScript's \s matches U+FEFF and Python's does not, so a lone BOM would read as empty
    // here and as content there. Swapped for the content marker rather than left as a
    // documented divergence — one of those is what this whole function is a fix for.
    .replace(/\uFEFF/g, '\uFFFD');
  return !text.replace(/[\s\u0085\u001c-\u001f]+/g, ' ').trim();
};
